import { FormattedMessage, useIntl } from "react-intl";

type Props = {
  /** `-hint` landet auf der Hinweiszeile. */
  id?: string;
  label?: string;
  hint?: string;
  // Friendly "coming soon" treatment for features that aren't wired to a
  // backend yet (vs. the default, which marks a genuinely empty/no-data
  // state). Shows a blurred faux-content field + a "coming soon" badge.
  comingSoon?: boolean;
};

export function NoBackend({ id, label, hint, comingSoon = false }: Props) {
  const intl = useIntl();
  const resolvedLabel =
    label ?? intl.formatMessage({ id: "common.noBackend", defaultMessage: "No backend endpoint" });
  if (comingSoon) {
    return (
      <div id={id} className="relative overflow-hidden rounded-md bg-ink/[0.02] px-4 py-7 ring-1 ring-ink/10">
        {/* Blurred skeleton hinting at the content that's on the way. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-50 blur-[5px]"
        >
          <div className="h-2.5 w-2/3 rounded-full bg-ink/10" />
          <div className="h-2.5 w-1/2 rounded-full bg-ink/10" />
          <div className="h-2.5 w-3/5 rounded-full bg-ink/10" />
        </div>
        <div className="relative flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-0.5 text-smycles-micro font-semibold tracking-[0.18em] text-ink-accent">
            <FormattedMessage id="common.comingSoon" defaultMessage="Coming soon" />
          </span>
          <div className="mt-2 text-smycles-meta font-medium text-ink">{resolvedLabel}</div>
        </div>
      </div>
    );
  }

  return (
    <div id={id} className="rounded-md bg-ink/[0.02] px-4 py-6 text-center ring-1 ring-ink/15">
      <div className="text-smycles-meta text-ink-muted">{resolvedLabel}</div>
      {hint ? <div id={id && `${id}-hint`} className="mt-1 text-smycles-label text-ink-muted">{hint}</div> : null}
    </div>
  );
}
